import styled, { css } from "styled-components";
import { motion } from "framer-motion";
import { NavLink } from "react-router-dom";
import { BP_TABLET } from "../../styles/canonical/breakpoints";

// Larguras canônicas (Bloco D): --sidebar-width-expanded / --sidebar-width-collapsed.
export const SIDEBAR_WIDTH_EXPANDED = 248;
export const SIDEBAR_WIDTH_COLLAPSED = 64;
export const TOPBAR_HEIGHT = 56;
// Drawer threshold — Bloco D: abaixo de 1024px a sidebar fixa some e o
// drawer overlay assume (acionado pelo burger). (Antes era 920px.)
export const MOBILE_BREAKPOINT = BP_TABLET;

export const SidebarRoot = styled.aside`
  position: fixed;
  /* Desce junto com o conteudo quando a faixa de MFA (P2) esta visivel.
     --safe-area-top empurra abaixo do notch no PWA iOS (0 fora do PWA). */
  top: calc(var(--topbar-height) + var(--safe-area-top, 0px) + var(--mfa-banner-h, 0px));
  left: 0;
  bottom: 0;
  width: ${({ $collapsed }) =>
    $collapsed
      ? "var(--sidebar-width-collapsed)"
      : "var(--sidebar-width-expanded)"};
  background: var(--bg-elevated);
  border-right: 1px solid var(--border);
  display: flex;
  flex-direction: column;
  z-index: 90;
  transition: width 0.18s ease;
  overflow: hidden;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    display: none;
  }
`;

export const SidebarDrawer = styled(motion.aside)`
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  /* Drawer mobile ocupa top:0 full-height; reserva a inset do notch (PWA iOS)
     pro cabecalho do drawer nao ficar sob a Dynamic Island. */
  padding-top: var(--safe-area-top, 0px);
  width: min(280px, 90vw);
  background: var(--bg-elevated);
  border-right: 1px solid var(--border);
  display: flex;
  flex-direction: column;
  z-index: 150;
  box-shadow: var(--shadow-3);

  @media (min-width: ${MOBILE_BREAKPOINT + 1}px) {
    display: none;
  }
`;

export const SidebarOverlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(2px);
  z-index: 149;

  @media (min-width: ${MOBILE_BREAKPOINT + 1}px) {
    display: none;
  }
`;

export const ScrollArea = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 4px 0 16px;

  /* Scrollbar discreto */
  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.border};
    border-radius: 2px;
  }
`;

export const SidebarHeader = styled.div`
  display: flex;
  align-items: center;
  /* Logo foi removido (ja existe na topbar global). Botao de colapsar
     fica alinhado a direita, com altura/padding minimos para nao
     gerar faixa visual cinza vazia. */
  justify-content: flex-end;
  padding: 6px 8px;
  min-height: 36px;
`;

export const MobileBrand = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const CollapseButton = styled.button`
  appearance: none;
  background: none;
  border: 1px solid transparent;
  border-radius: 8px;
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.textSecondary};
  cursor: pointer;
  transition: all 0.15s ease;
  flex-shrink: 0;

  &:hover {
    background: ${({ theme }) => theme.surfaceAlt};
    color: ${({ theme }) => theme.textPrimary};
  }

  &:focus-visible {
    border-color: ${({ theme }) => theme.accent};
    outline: none;
  }
`;

export const SectionLabel = styled.div`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.textTertiary || theme.textSecondary};
  padding: 14px 18px 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  /* No estado colapsado a separacao vira a cargo de <RailSectionDivider> — o
     label some pra nao gerar ruido. */
  ${({ $collapsed }) =>
    $collapsed &&
    css`
      display: none;
    `}
`;

/* Linha divisora entre secoes no estado colapsado (entre PAGINAS_GLOBAIS,
   modulos, ADMIN). Em expandido, SectionLabel faz o trabalho visual. */
export const RailSectionDivider = styled.div`
  display: none;

  ${({ $collapsed }) =>
    $collapsed &&
    css`
      display: block;
      margin: 12px 14px;
      height: 1px;
      background: ${({ theme }) => theme.border};
      opacity: 0.7;
    `}
`;

/* === MINI-RAIL: modulo virou um icone + popout (estilo Slack/VSCode) === */

export const RailItemWrap = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  margin: 2px 0;
`;

export const RailIconButton = styled.button`
  appearance: none;
  background: ${({ theme, $active }) =>
    $active ? theme.surfaceAlt : "transparent"};
  border: 1px solid transparent;
  border-radius: 10px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme, $active }) => ($active ? theme.accent : theme.textSecondary)};
  cursor: pointer;
  position: relative;
  transition: background 0.12s ease, color 0.12s ease;

  &:hover {
    background: ${({ theme }) => theme.surfaceAlt};
    color: ${({ theme, $active }) => ($active ? theme.accent : theme.textPrimary)};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.accent};
    outline-offset: 2px;
  }

  /* Indicador de modulo ativo: barra acento a esquerda. */
  ${({ $active, theme }) =>
    $active &&
    css`
      &::before {
        content: "";
        position: absolute;
        left: -12px;
        top: 8px;
        bottom: 8px;
        width: 3px;
        border-radius: 2px;
        background: ${theme.accent};
      }
    `}
`;

export const RailFlyout = styled(motion.div)`
  /* position: fixed para escapar do overflow:hidden da SidebarRoot/ScrollArea.
     Posicionamento via style inline (top/left) calculado pelo getBoundingClientRect
     do icone no ModuleRailItem. Renderizado via Portal em document.body. */
  position: fixed;
  min-width: 240px;
  max-width: 280px;
  max-height: 80vh;
  /* overflow-y: visible para que a ponte invisivel a esquerda (criada via
     pseudo-elemento abaixo) nao seja cortada. Conteudo interno rola via
     wrapper interno se necessario — na pratica nao excede 80vh. */
  overflow-y: auto;
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.22);
  padding: 6px 0;
  z-index: 300;

  /* Seta apontando pra o icone (sempre alinhada com o topo do flyout). */
  &::before {
    content: "";
    position: absolute;
    left: -7px;
    top: 14px;
    width: 0;
    height: 0;
    border-top: 7px solid transparent;
    border-bottom: 7px solid transparent;
    border-right: 7px solid ${({ theme }) => theme.border};
  }
  &::after {
    content: "";
    position: absolute;
    left: -6px;
    top: 14px;
    width: 0;
    height: 0;
    border-top: 7px solid transparent;
    border-bottom: 7px solid transparent;
    border-right: 7px solid ${({ theme }) => theme.surface};
  }
`;

/**
 * Removido em 2026-05-28 (retrabalho #11): o componente
 * RailFlyoutHoverBridge anterior nao funcionava — era cortado pelo
 * `overflow-y: auto` do RailFlyout. Substituido pela solucao mais
 * simples: FLYOUT_OFFSET_X = -2 em Sidebar/index.jsx faz o flyout
 * overlapar 2px com o icone, eliminando o gap fisicamente. Sem
 * dependencia de pseudo-elemento ou bridge invisivel.
 *
 * Export mantido como `null` apenas para nao quebrar imports legados
 * caso ainda existam — pode ser deletado em proxima limpeza.
 */
export const RailFlyoutHoverBridge = null;

export const FlyoutHeader = styled.div`
  padding: 8px 16px 10px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.textTertiary || theme.textSecondary};
  border-bottom: 1px solid ${({ theme }) => theme.border};
  margin-bottom: 4px;
`;

export const FlyoutGroup = styled.div`
  padding: 2px 0 4px;

  & + & {
    border-top: 1px solid ${({ theme }) => theme.border};
    margin-top: 4px;
    padding-top: 6px;
  }
`;

export const FlyoutGroupLabel = styled.div`
  padding: 4px 16px 2px;
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.textTertiary || theme.textSecondary};
  opacity: 0.75;
`;

export const FlyoutItem = styled(NavLink)`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 7px 16px;
  font-size: 13px;
  color: ${({ theme }) => theme.textSecondary};
  text-decoration: none;
  transition: background 0.12s ease, color 0.12s ease;

  .icon {
    display: inline-flex;
    color: ${({ theme }) => theme.textTertiary || theme.textSecondary};
    flex-shrink: 0;
  }

  &:hover {
    background: ${({ theme }) => theme.surfaceAlt};
    color: ${({ theme }) => theme.textPrimary};
    .icon {
      color: ${({ theme }) => theme.textPrimary};
    }
  }

  &.active {
    background: ${({ theme }) => theme.surfaceAlt};
    color: ${({ theme }) => theme.accent};
    font-weight: 600;
    .icon {
      color: ${({ theme }) => theme.accent};
    }
  }
`;

export const GroupAccordion = styled.div`
  display: flex;
  flex-direction: column;
`;

export const GroupHeader = styled.button`
  appearance: none;
  background: none;
  border: none;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 7px 14px 7px 18px;
  width: 100%;
  cursor: pointer;
  color: ${({ theme }) => theme.textSecondary};
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-align: left;
  transition: color 0.12s ease, background 0.12s ease;

  &:hover {
    color: ${({ theme }) => theme.textPrimary};
    background: ${({ theme }) => theme.surfaceAlt};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.accent};
    outline-offset: -2px;
  }

  .label {
    flex: 1;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    font-size: 10.5px;
  }

  .chevron {
    display: inline-flex;
    transition: transform 0.18s ease;
    color: ${({ theme }) => theme.textTertiary || theme.textSecondary};
  }

  &[data-open="true"] .chevron {
    transform: rotate(90deg);
  }

  /* Redesenho do Legado (2026-08-04): grupo com icone se veste de ITEM —
     mesmo porte visual de Início/Clientes, sem caixa alta. A variante liga
     via data-item, entao os demais modulos que usam cabecalho em caixa alta
     continuam exatamente como estavam. */
  .icone {
    display: inline-flex;
    align-items: center;
    color: inherit;
  }

  &[data-item="true"] {
    padding: 8px 14px 8px 18px;

    .label {
      text-transform: none;
      letter-spacing: 0;
      font-size: 13px;
      font-weight: 500;
    }
  }
`;

/* Sub-grupo (Projetos Executivos > Análises/Painéis/Base): texto simples,
   indentado alem do grupo pai, sem icone — fiel ao mockup. */
export const SubGroupHeader = styled.button`
  appearance: none;
  background: none;
  border: none;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px 6px 44px;
  width: 100%;
  cursor: pointer;
  color: ${({ theme }) => theme.textSecondary};
  font-size: 12.5px;
  font-weight: 500;
  text-align: left;
  transition: color 0.12s ease, background 0.12s ease;

  &:hover {
    color: ${({ theme }) => theme.textPrimary};
    background: ${({ theme }) => theme.surfaceAlt};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.accent};
    outline-offset: -2px;
  }

  .label {
    flex: 1;
  }

  .chevron {
    display: inline-flex;
    transition: transform 0.18s ease;
    color: ${({ theme }) => theme.textTertiary || theme.textSecondary};
  }

  &[data-open="true"] .chevron {
    transform: rotate(90deg);
  }
`;

export const SubGroupItems = styled(motion.div)`
  overflow: hidden;
  /* um passo de indentacao alem dos itens do grupo pai */
  padding-left: 12px;
`;

export const GroupItems = styled(motion.div)`
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const navItemBase = css`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 16px 8px ${({ $nested }) => ($nested ? "28px" : "16px")};
  margin: 1px 8px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.textSecondary};
  text-decoration: none;
  white-space: nowrap;
  overflow: hidden;
  transition: color 0.12s ease, background 0.12s ease;
  position: relative;

  .icon {
    display: inline-flex;
    flex-shrink: 0;
    width: 18px;
    justify-content: center;
    color: ${({ theme }) => theme.textTertiary || theme.textSecondary};
  }

  .label {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &:hover {
    background: var(--surface-hover);
    color: var(--ink);

    .icon {
      color: var(--ink);
    }
  }

  &.active {
    background: var(--accent-soft);
    color: var(--accent);
    font-weight: 600;

    .icon {
      color: var(--accent);
    }

    &::before {
      content: "";
      position: absolute;
      left: -8px;
      top: 6px;
      bottom: 6px;
      width: 3px;
      border-radius: 2px;
      background: var(--accent);
    }
  }

  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: -2px;
  }
`;

export const SidebarNavLink = styled(NavLink)`
  ${navItemBase}
`;

export const SidebarTopItem = styled(NavLink)`
  ${navItemBase}
  padding-left: 18px;
  font-weight: 500;
`;

/**
 * UI.5 (Bloco UI, auditoria 2026-05-23): Sidebar Favorites section.
 *
 * Aparece no topo da sidebar (acima dos modulos top-level), expansivel.
 * So aparece em modo expandido + se ha pelo menos 1 favorito.
 *
 * StarButton: botaozinho de estrela inline em cada NavItem expandido para
 * marcar/desmarcar favorito. Posicionado no canto direito do link, oculto
 * por default, aparece em hover ou quando ja eh favorito.
 */
export const FavoritesSection = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 1px solid ${({ theme }) => theme.border};
`;

export const FavoritesHeader = styled.button`
  appearance: none;
  background: none;
  border: none;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 14px 9px 16px;
  width: 100%;
  cursor: pointer;
  color: ${({ theme }) => theme.textTertiary || theme.textSecondary};
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  text-align: left;
  transition: color 0.12s ease;

  &:hover { color: ${({ theme }) => theme.accent}; }

  .icon {
    display: inline-flex;
    color: ${({ theme }) => theme.accent};
  }

  .label { flex: 1; }

  .chevron {
    display: inline-flex;
    transition: transform 0.18s ease;
    transform: ${({ "data-open": open }) => (open ? "rotate(90deg)" : "rotate(0deg)")};
  }
`;

/**
 * Wrapper customizado para NavItem favorito — alinha estrela a direita.
 * Replica `navItemBase` (sem importar pra evitar dependencia circular).
 */
export const NavItemFavoritoRow = styled.div`
  display: flex;
  align-items: stretch;
  position: relative;

  /* O NavLink interno ocupa todo o espaco; o botao estrela sobrepoe */
  & > a {
    flex: 1;
    padding-right: 36px; /* espaco para o botao estrela */
  }
`;

export const StarButton = styled.button`
  position: absolute;
  right: 6px;
  top: 50%;
  transform: translateY(-50%);
  appearance: none;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 6px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme, $ativo }) =>
    $ativo ? theme.accent : theme.textTertiary || theme.textSecondary};
  opacity: ${({ $ativo }) => ($ativo ? 1 : 0)};
  transition: opacity 0.15s ease, color 0.15s ease, background 0.15s ease;

  /* Aparece no hover do row pai (linha do NavItem) */
  ${NavItemFavoritoRow}:hover & {
    opacity: 1;
  }

  &:hover {
    color: ${({ theme }) => theme.accent};
    background: ${({ theme }) => theme.surfaceAlt};
  }
`;

export const SidebarFooter = styled.div`
  padding: 10px 14px 12px;
  border-top: 1px solid ${({ theme }) => theme.border};
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const UnitTag = styled.div`
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.textTertiary || theme.textSecondary};
  padding: 2px 4px;
  /* Nome da empresa longo não pode quebrar a barra: corta com reticências
     (o title mostra inteiro). */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/* === TOP-LEVEL ACCORDION: categorias colapsaveis (Legado, Comercial, Producao, etc) === */

export const TopLevelAccordion = styled.div`
  display: flex;
  flex-direction: column;
  /* Separa visualmente uma categoria da outra */
  & + & {
    margin-top: 2px;
  }
`;

// Badge inline em itens top-level — exibe contador (ex.: WhatsApp nao-lidas).
// Tone laranja/warning: chama atencao sem alarme; combina com paleta canonica.
export const SidebarBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: ${({ theme }) => theme.warning || "#f97316"};
  color: ${({ theme }) => theme.warningContrast || "#fff"};
  font-size: 10.5px;
  font-weight: 700;
  line-height: 1;
  flex-shrink: 0;
`;

export const TopLevelHeader = styled.button`
  appearance: none;
  background: ${({ $active }) =>
    $active ? "var(--accent-soft)" : "none"};
  border: none;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 14px 9px 16px;
  width: 100%;
  cursor: pointer;
  color: ${({ $active }) =>
    $active ? "var(--accent)" : "var(--ink)"};
  font-size: 12.5px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-align: left;
  transition: color 0.12s ease, background 0.12s ease;
  position: relative;

  .icon {
    display: inline-flex;
    flex-shrink: 0;
    width: 18px;
    justify-content: center;
    color: ${({ $active }) =>
      $active ? "var(--accent)" : "var(--ink-2)"};
  }

  .label {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .chevron {
    display: inline-flex;
    transition: transform 0.18s ease;
    color: ${({ theme }) => theme.textTertiary || theme.textSecondary};
    flex-shrink: 0;
  }

  &[data-open="true"] .chevron {
    transform: rotate(90deg);
  }

  &:hover {
    background: ${({ theme }) => theme.surfaceAlt};
    color: ${({ theme, $active }) =>
      $active ? theme.accent : theme.textPrimary};

    .icon {
      color: ${({ theme, $active }) =>
        $active ? theme.accent : theme.textPrimary};
    }
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.accent};
    outline-offset: -2px;
  }

  /* Indicador de categoria ativa (rota atual dentro dela) */
  ${({ $active, theme }) =>
    $active &&
    css`
      &::before {
        content: "";
        position: absolute;
        left: 0;
        top: 6px;
        bottom: 6px;
        width: 3px;
        border-radius: 2px;
        background: ${theme.accent};
      }
    `}
`;

export const TopLevelContent = styled(motion.div)`
  overflow: hidden;
  display: flex;
  flex-direction: column;
  padding-bottom: 4px;
`;

export const Tooltip = styled.span`
  position: absolute;
  left: ${SIDEBAR_WIDTH_COLLAPSED + 6}px;
  top: 50%;
  transform: translateY(-50%);
  background: ${({ theme }) => theme.textPrimary};
  color: ${({ theme }) => theme.surface};
  padding: 5px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.15s ease;
  z-index: 200;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
`;
